import { berechneSummen, type ProtokollData, type Summen } from '@wertermittlung/shared';
import { api, type KatalogResponse, type User } from './api.js';

/** Globaler App-Zustand + Autosave mit Offline-Fallback (localStorage). */

export interface Session {
  user: User;
  katalog: KatalogResponse;
}

export type SaveState = 'gespeichert' | 'speichert' | 'offline' | 'fehler';

export interface ProtokollSession {
  id: string;
  status: 'entwurf' | 'abgeschlossen';
  data: ProtokollData;
  faktor: number;
  jahr: number;
  saveState: SaveState;
  readonly: boolean;
}

const OFFLINE_KEY = (id: string) => `we-offline-${id}`;
let saveTimer: ReturnType<typeof setTimeout> | undefined;
let saveListeners: ((s: SaveState) => void)[] = [];

export function onSaveState(fn: (s: SaveState) => void): void {
  saveListeners.push(fn);
}
export function resetSaveListeners(): void {
  saveListeners = [];
}
function setSaveState(p: ProtokollSession, s: SaveState): void {
  p.saveState = s;
  for (const fn of saveListeners) fn(s);
}

export function summen(p: ProtokollSession): Summen {
  return berechneSummen(p.data, p.faktor, p.jahr);
}

/** Debounced-Autosave; bei Netzausfall lokale Sicherung + Retry bei 'online'. */
export function speichern(p: ProtokollSession): void {
  if (p.readonly) return;
  clearTimeout(saveTimer);
  setSaveState(p, 'speichert');
  saveTimer = setTimeout(() => void speichernJetzt(p), 800);
}

export async function speichernJetzt(p: ProtokollSession): Promise<boolean> {
  if (p.readonly) return true;
  try {
    const res = await api.protokollSpeichern(p.id, p.data);
    p.faktor = res.faktor;
    p.jahr = res.jahr;
    localStorage.removeItem(OFFLINE_KEY(p.id));
    setSaveState(p, 'gespeichert');
    return true;
  } catch (err) {
    if (err instanceof TypeError) {
      // Netzwerkfehler → lokal sichern, später synchronisieren
      try {
        localStorage.setItem(OFFLINE_KEY(p.id), JSON.stringify(p.data));
        setSaveState(p, 'offline');
      } catch {
        setSaveState(p, 'fehler');
      }
      return false;
    }
    setSaveState(p, 'fehler');
    return false;
  }
}

/** Lädt ein Protokoll; lokale Offline-Sicherung hat Vorrang und wird synchronisiert. */
export async function ladeProtokoll(id: string): Promise<ProtokollSession> {
  const res = await api.protokoll(id);
  const session: ProtokollSession = {
    id,
    status: res.status,
    data: res.data,
    faktor: res.faktor,
    jahr: res.jahr,
    saveState: 'gespeichert',
    readonly: res.status !== 'entwurf',
  };
  const offline = localStorage.getItem(OFFLINE_KEY(id));
  if (offline && !session.readonly) {
    session.data = JSON.parse(offline) as ProtokollData;
    void speichernJetzt(session);
  }
  return session;
}

export function registriereOnlineSync(p: ProtokollSession): () => void {
  const handler = () => {
    if (localStorage.getItem(OFFLINE_KEY(p.id))) void speichernJetzt(p);
  };
  window.addEventListener('online', handler);
  return () => window.removeEventListener('online', handler);
}
